import { Component, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/material/material.module';
import { AuthService } from 'src/app/auth/services/auth.service';
import { TenantService } from 'src/app/tenant/services/tenant.service';
import { UserRole } from 'src/app/auth/models/user-roles';
import { ITenantDTO } from 'src/app/shared/models/tenant';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-tenant-switcher',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './tenant-switcher.component.html'
})
export class TenantSwitcherComponent {
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly router = inject(Router);

  private readonly userData = toSignal(this.authService.userData$);

  readonly canSwitchTenants = computed(() => {
    const userData = this.userData();
    if (!userData) return false;
    
    if (this.authService.userHasRoleAndRoleIsEnabled(UserRole.SuperUser)) {
      return true;
    }
    
    // All roles can switch tenants if they have multiple tenants
    return userData.tenants && userData.tenants.length > 1;
  });

  readonly isSuperUser = computed(() => {
    return this.authService.userHasRoleAndRoleIsEnabled(UserRole.SuperUser);
  });

  readonly currentTenantCode = computed(() => {
    return this.tenantService.tenantCode() || 'No Tenant';
  });

  readonly currentTenantDisplayName = computed(() => {
    const tenant = this.tenantService.tenantDetails();
    return tenant?.name || this.tenantService.tenantCode() || 'No Tenant';
  });

  readonly availableTenants = computed(() => {
    const userData = this.userData();
    return userData?.tenants || [];
  });

  readonly currentTenant = computed(() => {
    return this.tenantService.tenantDetails();
  });

  readonly tenantsLoading = computed(() => {
    const userData = this.userData();
    if (!userData || !userData.tenants) return false;
    
    // If we have tenants but they don't have names, they're still loading
    return userData.tenants.length > 0 && userData.tenants.every(t => !t.name || t.name === '');
  });

  switchToTenant(tenant: ITenantDTO) {
    if (tenant.shortCode) {
      // Use the tenant service to explicitly switch, then reload the route
      this.tenantService.switchToTenant(tenant.shortCode).subscribe(() => {
        this.router.navigate([tenant.shortCode, 'admin']);
      });
    }
  }

  goToTenantSettings() {
    const currentCode = this.tenantService.tenantCode();
    if (currentCode) {
      this.router.navigate([currentCode, 'admin', 'tenants']);
    }
  }
}

